// What the player has seen of a zone: one byte per tile (ground + a coarse object
// class) for every chunk ever streamed, kept after the chunk unloads. Drives the world map; hand-made maps' memories are
// saved.
import { ObjectType, TileLayer } from '../../../shared/src/constants';
import { tiles } from './content';
import { InvalidDataError, deflate, inflate } from './bytes';
import { Chunk, chunkKey, chunkOfTile, chunkSize, tilesPerChunk } from './map';
import { log } from './log';

export enum Feature { None = 0, Tree = 1, Rock = 2, DeadWood = 3, Entrance = 4, Exit = 5 }

function featureOfObject(o: number): Feature {
  switch (o) {
    case ObjectType.Tree: case ObjectType.Pine: case ObjectType.Bush: return Feature.Tree;
    case ObjectType.Rock: case ObjectType.AshRock: case ObjectType.Rubble: case ObjectType.Crystal: return Feature.Rock;
    case ObjectType.DeadTree: case ObjectType.Stump: return Feature.DeadWood;
    case ObjectType.CaveEntrance: return Feature.Entrance;
    case ObjectType.CaveExit: return Feature.Exit;
    default: return Feature.None;
  }
}

const magic = 'AHMAP1';

export class MapMemory {
  static encode = (g: number, o: number) => (tiles().baseGround(g) & 0x0f) | (featureOfObject(o) << 4);
  static groundOf = (code: number) => code & 0x0f;
  static featureOf = (code: number): Feature => code >> 4;

  readonly chunks = new Map<string, { cx: number; cy: number; data: Uint8Array }>();
  private readonly dirty = new Set<string>();
  readonly caveEntrances = new Set<string>();
  widthTiles = 0;
  heightTiles = 0;
  /** Bumped on every change (for autosave). */
  version = 0;

  /** Chunks whose map picture needs redrawing, as [cx, cy]. */
  takeDirty(): [number, number][] {
    const list = [...this.dirty].map(k => k.split(',').map(Number) as [number, number]);
    this.dirty.clear();
    return list;
  }

  markAllDirty(): void { for (const k of this.chunks.keys()) this.dirty.add(k); }

  record(chunk: Chunk): void {
    const key = chunkKey(chunk.cx, chunk.cy);
    let entry = this.chunks.get(key);
    if (!entry) this.chunks.set(key, entry = { cx: chunk.cx, cy: chunk.cy, data: new Uint8Array(tilesPerChunk) });
    let changed = false;
    for (let i = 0; i < tilesPerChunk; i++) {
      let material = chunk.ground[i];
      for (const ground of [chunk.ground[i], chunk.ground2[i], chunk.ground3[i]]) {
        const base = tiles().get(TileLayer.Ground, ground)?.baseGround || (ground <= 14 ? ground : 0);
        if (base) material = base;
      }
      const objectMaterial = tiles().get(TileLayer.Object, chunk.objects[i])?.baseGround;
      if (objectMaterial) material = objectMaterial;
      const code = MapMemory.encode(material, chunk.objects[i]);
      if (entry.data[i] === code) continue;
      entry.data[i] = code;
      changed = true;
      this.trackEntrance(chunk.cx, chunk.cy, i, code);
    }
    if (!changed) return;
    this.dirty.add(key);
    this.version++;
  }

  recordTile(tx: number, ty: number, g: number, o: number): void {
    const cx = chunkOfTile(tx), cy = chunkOfTile(ty);
    const entry = this.chunks.get(chunkKey(cx, cy));
    if (!entry) return;
    const i = Chunk.index(tx - cx * chunkSize, ty - cy * chunkSize);
    const code = MapMemory.encode(g, o);
    if (entry.data[i] === code) return;
    entry.data[i] = code;
    this.trackEntrance(cx, cy, i, code);
    this.dirty.add(chunkKey(cx, cy));
    this.version++;
  }

  private trackEntrance(cx: number, cy: number, i: number, code: number): void {
    const t = `${cx * chunkSize + (i % chunkSize)},${cy * chunkSize + Math.floor(i / chunkSize)}`;
    if (MapMemory.featureOf(code) === Feature.Entrance) this.caveEntrances.add(t);
    else this.caveEntrances.delete(t);
  }

  /** The explored map as a file (deflated: the magic, size, then each chunk's coordinates and bytes). */
  async toBytes(): Promise<Uint8Array> {
    const size = 7 + 12 + this.chunks.size * (4 + tilesPerChunk);
    const out = new Uint8Array(size);
    const view = new DataView(out.buffer);
    out[0] = magic.length;
    for (let i = 0; i < magic.length; i++) out[1 + i] = magic.charCodeAt(i);
    view.setInt32(7, this.widthTiles, true);
    view.setInt32(11, this.heightTiles, true);
    view.setInt32(15, this.chunks.size, true);
    let at = 19;
    for (const { cx, cy, data } of this.chunks.values()) {
      view.setInt16(at, cx, true);
      view.setInt16(at + 2, cy, true);
      out.set(data, at + 4);
      at += 4 + tilesPerChunk;
    }
    return deflate(out);
  }

  /** Adds a saved map's chunks (ones seen since it started loading win). Unreadable saves are ignored. */
  async load(bytes: Uint8Array | null, what = 'map'): Promise<void> {
    if (!bytes) return;
    try {
      const raw = await inflate(bytes, 64 * 1024 * 1024);
      const view = new DataView(raw.buffer, raw.byteOffset, raw.byteLength);
      if (raw.length < 19 || raw[0] !== magic.length || String.fromCharCode(...raw.subarray(1, 7)) !== magic) return;
      const n = view.getInt32(15, true);
      let at = 19;
      for (let k = 0; k < n && at + 4 + tilesPerChunk <= raw.length; k++) {
        const cx = view.getInt16(at, true), cy = view.getInt16(at + 2, true);
        const data = raw.slice(at + 4, at + 4 + tilesPerChunk);
        at += 4 + tilesPerChunk;
        const key = chunkKey(cx, cy);
        if (this.chunks.has(key)) continue;
        this.chunks.set(key, { cx, cy, data });
        for (let i = 0; i < tilesPerChunk; i++) if (MapMemory.featureOf(data[i]) === Feature.Entrance) this.trackEntrance(cx, cy, i, data[i]);
        this.dirty.add(key);
      }
    } catch (e) {
      if (!(e instanceof InvalidDataError)) throw e;
      log.warn(`Saved ${what} is unreadable; starting a fresh map (${e.message})`);
    }
  }
}

